import './TicketScanner.css';
import classNames from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import { Button } from '../../components/Button/Button';
import { Container } from '../../components/Container/Container';
import { t } from '../../locales/translate';
import { SwipeContainer } from './SwipeContainer';
import { useSearchParam } from '../../model/history';
import {
  addActivityLogItem,
  updateActivityLogItem,
  checkTicketIn,
  Ticket,
  TicketScannerActivityLogItem,
  checkTicketOut,
  useTicketScanner,
  getVenueAddress,
} from '../../model/ticketScanner';
import { formatDate, formatTime } from '../../util/formatters';
import { ActivityLog } from './ActivityLog';
import { QRReader } from './QRReader';
import { ScanResult } from './ScanResult';
import { ManualInput } from './ManualInput';
import { guid } from 'redux-database/dist/util';
import { useMeta } from '../../model/meta';
import { Lobby } from './Lobby';
import { deviceBarcodeReader, DeviceBarcodeReader } from './DeviceBarcodeReader';
import { ScannerLoader } from './ScannerLoader';
import { CollapsibleHeader } from './CollapsibleHeader';
import { Stack } from '../../components/Stack/Stack';
import { LoadingPage } from '../../components/LoadingPage/LoadingPage';

const COLLAPSED_CONTENT_MAX_HEIGHT = 100;

function findTicket(tickets: Ticket[], scannedNumber: string): Ticket | null {
  if (!scannedNumber) {
    return null;
  }

  return tickets.find((e) => scannedNumber.trim() === e.validationCode.trim()) ?? null;
}

const createActivityLogItem = (
  ticket: Ticket | null,
  scannerCode: string,
  eventId: string
): TicketScannerActivityLogItem => {
  const baseLogItem: Pick<
    TicketScannerActivityLogItem,
    'id' | 'code' | 'scannedAt' | 'isManual' | 'eventId' | 'needsSync'
  > = {
    id: guid(),
    code: scannerCode,
    scannedAt: new Date(),
    isManual: false,
    eventId: eventId,
    needsSync: false,
  };

  if (!ticket) {
    return {
      ...baseLogItem,
      type: 'invalid',
    };
  }

  if (ticket.usedAt) {
    return {
      ...baseLogItem,
      type: 'already_checked',
      wasCheckedOut: false,
      isManual: false,
      ticketId: ticket.id,
    };
  }

  return { ...baseLogItem, type: 'valid', ticketId: ticket.id, scannedAt: new Date(), needsSync: true };
};

const resultTypeToScannerStateMap: Record<TicketScannerActivityLogItem['type'], ScannerState> = {
  invalid: 'error',
  valid: 'success',
  already_checked: 'checked',
};

export type ScannerState =
  | 'initial' // app just got started, waiting to init reader
  | 'ready' // camera is ready, waiting to start scanning
  | 'scanning' // camera is actively scanning for a code
  | 'success' // scanned ticket was valid
  | 'error' // scanned ticket was invalid
  | 'checked'; // scanned ticket was checked in before

export function TicketScanner(): JSX.Element | null {
  const secureEventId = useSearchParam('event_id') || undefined;
  const scannerName = useSearchParam('scanner_name') || '';
  const { event, tickets, activityLog, offline, loadingTickets, loadingEvent } = useTicketScanner({
    scannerName,
    secureEventId,
  });
  const [scanningState, setScanningState] = useState<ScannerState>('initial');
  const [lastScannedCode, setLastScannedCode] = useState<string | null>(null);
  const [isManualInput, setIsManualInput] = useState(false);

  const firstTicketFromTheLog = activityLog[0]?.ticket;

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, []);

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    if (!['ready', 'initial', 'checked', 'scanning'].includes(scanningState)) {
      timeoutId = setTimeout(() => {
        setScanningState(deviceBarcodeReader.available ? 'ready' : 'scanning');
      }, 2000);
    }
    return () => clearTimeout(timeoutId);
  }, [scanningState, lastScannedCode]);

  useMeta({ title: [event?.artist, event?.name].filter(Boolean).join(' - '), themeColor: '#2f3b6d' });

  const [isHeaderCollapsed, setIsHeaderCollapsed] = useState(false);

  const scannerContent = useRef<HTMLDivElement>(null);
  const scannerContentHeight = useRef(0);

  if (scannerContentHeight.current === 0 && scannerContent.current) {
    scannerContentHeight.current = scannerContent.current.getBoundingClientRect().height;
  }

  if (loadingEvent) {
    return <LoadingPage title={t.actions.loading('')} />;
  }

  if (!event) {
    return <Lobby />;
  }

  if (loadingTickets) {
    return (
      <Container isMobile className="ticket-scanner">
        <ScannerLoader totalCount={event.salesStats.sold} loadedCount={tickets.length} />
      </Container>
    );
  }

  const handleScanTicket = (code: string, clearManualInput?: () => void): void => {
    // prevent scanning the same code twice in a short time
    if (scanningState === 'success' && lastScannedCode === code) return;

    const scannedTicket = findTicket(tickets, code);
    const activityLogItem = createActivityLogItem(scannedTicket, code, event.id);

    if (activityLogItem.type === 'already_checked') {
      if (clearManualInput) {
        clearManualInput();
      }
    }

    setScanningState(resultTypeToScannerStateMap[activityLogItem.type]);
    setLastScannedCode(code);
    addActivityLogItem(activityLogItem);

    if (activityLogItem.type === 'valid' && scannedTicket) {
      if (clearManualInput) {
        clearManualInput();
      }
      deviceBarcodeReader.beep('success');
      checkTicketIn({ ticket: scannedTicket, validatedBy: scannerName });
    } else {
      deviceBarcodeReader.beep('fail');
    }
  };

  const handleCheckout = (): void => {
    if (!firstTicketFromTheLog) return;

    const [firstLogItem] = activityLog;

    setScanningState('ready');

    if (firstLogItem?.type === 'already_checked') {
      updateActivityLogItem({ ...firstLogItem, wasCheckedOut: true, scannedAt: new Date(), needsSync: true });
    }

    checkTicketOut({ ticket: firstTicketFromTheLog });
  };

  return (
    <div className="ticket-scanner">
      <div className="ticket-scanner__content" ref={scannerContent}>
        {event && (
          <CollapsibleHeader
            title={[event.artist, event.name].filter(Boolean).join(' - ')}
            onCollapseToggle={() => {
              setIsHeaderCollapsed((s) => !s);
            }}
          >
            <p>{getVenueAddress(event.venue)}</p>
            <p>
              {formatDate(event.happeningFrom, undefined, { weekday: 'long' })} {event.stageTime} - {event.sku}
            </p>
          </CollapsibleHeader>
        )}
        <Container isMobile>
          <Stack>
            <SwipeContainer
              shouldShowScanner={!isManualInput}
              onManualModeEnter={() => {
                deviceBarcodeReader.stop();
                setScanningState('ready');
                setIsManualInput(true);
              }}
              onManualModeLeave={() => {
                setIsManualInput(false);
              }}
            >
              {[
                () => (
                  <div key="scanner" className="ticket-scanner__scanner-content">
                    <div className="ticket-scanner__window">
                      <DeviceBarcodeReader onScanCode={handleScanTicket} />
                      <QRReader
                        codeTypes={event.scannerCodeTypes}
                        onVideoInitialized={() => {
                          setScanningState('ready');
                        }}
                        displayVideo={scanningState === 'scanning' || scanningState === 'success'}
                        isScanning={scanningState === 'scanning'}
                        onScanCode={handleScanTicket}
                      />
                      <ScanResult
                        state={scanningState}
                        checkInLocation={firstTicketFromTheLog?.validatedBy || ''}
                        checkInTime={formatTime(firstTicketFromTheLog?.usedAt ?? undefined, { seconds: true })}
                        onCheckout={handleCheckout}
                        key={lastScannedCode}
                      />
                    </div>
                  </div>
                ),
                (position) => {
                  return (
                    <ManualInput
                      key="manual"
                      onCodeSubmit={({ code, clearInput }) => {
                        handleScanTicket(code, clearInput);
                        setIsManualInput(false);
                      }}
                      isInputInvalid={scanningState === 'error'}
                      isInView={position === 'right'}
                    />
                  );
                },
              ]}
            </SwipeContainer>
            <div className="ticket-scanner__button-wrapper">
              <Button
                className={classNames('ticket-scanner__scan-button', {
                  active: scanningState === 'scanning',
                  transparent: isManualInput,
                })}
                title={
                  scanningState === 'scanning' ? t.page.ticketScanner.stopScanning : t.page.ticketScanner.startScanning
                }
                loading={scanningState === 'initial'}
                icon={scanningState === 'scanning' ? 'rounded-square' : 'camera'}
                onClick={() => {
                  setIsManualInput(false);

                  if (deviceBarcodeReader.available) {
                    if (scanningState === 'scanning') {
                      deviceBarcodeReader.stop();
                    } else {
                      deviceBarcodeReader.start();
                    }
                  }
                  setScanningState(scanningState === 'scanning' ? 'ready' : 'scanning');
                }}
              />
              <Button
                className={classNames('ticket-scanner__code-button', {
                  active: isManualInput,
                })}
                title="Ticketcode"
                loading={scanningState === 'initial'}
                icon="double-a"
                onClick={() => {
                  deviceBarcodeReader.stop();
                  setScanningState('ready');
                  setIsManualInput(true);
                }}
              />
            </div>
          </Stack>
        </Container>
      </div>
      {scannerContentHeight.current !== 0 && (
        <>
          <div
            className="ticket-scanner__spacer"
            style={{
              height: isHeaderCollapsed
                ? scannerContentHeight.current + COLLAPSED_CONTENT_MAX_HEIGHT
                : scannerContentHeight.current,
            }}
          ></div>
          <ActivityLog scannerName={scannerName} log={activityLog} offline={offline} />
        </>
      )}
    </div>
  );
}
